import LBox from "@components/LBox";
import LIcon from "@components/LIcon";
import LoadingCircle from "@components/LSuspense/LoadingCircle";
import LText from "@components/LText";
import { useTopBarHeight } from "@hooks/useTopBarHeight";
import { theme } from "@theme";
import useInternetStatus from "@utils/internetStatus";
import React, { useEffect, useState } from "react";
import { ViewStyle, StyleProp } from "react-native";
import Animated, {
  useSharedValue,
  useAnimatedStyle,
  withTiming,
  FadeIn,
} from "react-native-reanimated";

const DEFAULT_CONFIG = {
  extendedLoading: {
    delay: 500,
    fadeIn: {
      duration: 450,
    },
  },
  initialLoading: {
    delay: 0,
    fadeIn: {
      duration: 450,
    },
  },
};

export interface LoadingProps {
  extendedLoading?: {
    delay: number;
    fadeIn: {
      duration: number;
    };
  };
  loading?: {
    fadeIn: {
      duration: number;
    };
  };
  contentContainerStyle?: StyleProp<ViewStyle>;
  disableInitialFadeIn?: boolean;
}

const Loading = ({
  extendedLoading = DEFAULT_CONFIG.extendedLoading,
  loading = DEFAULT_CONFIG.initialLoading,
  contentContainerStyle,
  disableInitialFadeIn = false,
}: LoadingProps) => {
  const progress = useSharedValue(0);
  const [showExtended, setShowExtended] = useState(false);
  const topBarHeight = useTopBarHeight();
  const isConnected = useInternetStatus();

  const backgroundStyle = useAnimatedStyle(() => ({
    alignItems: "center",
    backgroundColor: theme.colors.transparent,
    flex: 1,
    justifyContent: "center",
    opacity: disableInitialFadeIn ? 1 : progress.value,
  }));

  useEffect(() => {
    progress.set(withTiming(1, { duration: loading.fadeIn.duration }));

    // Set a timeout to show ExtendedLoading after extendedLoading.delay ms
    const timer = setTimeout(() => {
      setShowExtended(true);
    }, extendedLoading.delay);

    return () => {
      clearTimeout(timer);
    };
  }, [extendedLoading.delay, loading.fadeIn.duration, progress]);

  return (
    <Animated.View style={[backgroundStyle, contentContainerStyle]}>
      {showExtended && (
        <Animated.View
          entering={FadeIn.duration(extendedLoading.fadeIn.duration)}
          style={{
            alignItems: "center",
            flexDirection: "row",
            justifyContent: "center",
            paddingBottom: topBarHeight,
          }}
        >
          {isConnected ? (
            <>
              <LoadingCircle delay={0} />
              <LoadingCircle delay={333} />
              <LoadingCircle delay={666} />
            </>
          ) : (
            <LBox flex={1} alignItems="center">
              <LIcon name="offline" size={42} mb="s3" />
              <LText variant="bodyL" mb={"l1"} textAlign="center">
                {`Unable to connect\nto the internet`}
              </LText>
            </LBox>
          )}
        </Animated.View>
      )}
    </Animated.View>
  );
};

export default Loading;
